import { z } from 'zod'
import { DateOrStringSchema as dateOrString } from './common'
import { LessonLevelEnum } from './lesson'

export const LevelTagCombinationSchema = z.object({
  id: z.string(),
  tagId: z.string().min(1),
  level: LessonLevelEnum,
})

/** API スキーマ（バックエンドバリデーション用） */
export const CourseSchema = z.object({
  title: z.string().min(3, { message: 'タイトルは3文字以上で入力してください。' }),
  description: z
    .string()
    .min(5, { message: 'コース説明は5文字以上で入力してください。' })
    .max(1000, { message: 'コース説明は1000文字以内で入力してください。' }),
  imageUrl: z.string().url({ message: '有効な画像URLを入力してください。' }).optional().nullable(),
  level: LessonLevelEnum,
  duration: z.string().min(1, { message: '学習期間の目安を入力してください。' }),
  isPublished: z.boolean().optional(),
})

export const CreateCourseSchema = CourseSchema.omit({ isPublished: true })
export const UpdateCourseSchema = CourseSchema.partial().extend({
  evaluationPrompt: z.string().max(200).nullable().optional(),
})

export type CourseInput = z.infer<typeof CourseSchema>
export type CreateCourseInput = z.infer<typeof CreateCourseSchema>
export type UpdateCourseInput = z.infer<typeof UpdateCourseSchema>

// ─── Lesson Order ─────────────────────────────────────────
export const UpdateLessonOrderSchema = z.object({
  lessonIds: z.array(z.string().uuid()).min(1),
})
export type UpdateLessonOrderInput = z.infer<typeof UpdateLessonOrderSchema>

export const LessonOrderItemSchema = z.object({
  id: z.string(),
  title: z.string(),
  level: z.string(),
  sortOrder: z.number().nullable(),
})
export type LessonOrderItem = z.infer<typeof LessonOrderItemSchema>

/** フォームスキーマ（フロントエンド LevelTagSelector 用） */
export const CourseFormSchema = z.object({
  title: z.string().min(3, { message: 'タイトルは3文字以上で入力してください。' }),
  description: z
    .string()
    .min(5, { message: 'コース説明は5文字以上で入力してください。' })
    .max(1000, { message: 'コース説明は1000文字以内で入力してください。' }),
  imageUrl: z.string().url({ message: '有効な画像URLを入力してください。' }).optional().nullable(),
  levelTagCombinations: z.array(LevelTagCombinationSchema).min(1, {
    message: 'レベル×タグの組み合わせを1つ以上追加してください。',
  }),
  duration: z.string().min(1, { message: '学習期間の目安を入力してください。' }),
  isPublished: z.boolean().optional(),
})

export type CourseFormInput = z.infer<typeof CourseFormSchema>

// ─── Response schemas (GET レスポンス形状) ───────────────

/** GET /api/courses — 一覧要素（withStats） */
export const CourseWithStatsSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  category: z.string(),
  level: z.string(),
  duration: z.string(),
  instructorId: z.string().nullable(),
  imageUrl: z.string().nullable(),
  isPublished: z.boolean(),
  markdownSummary: z.string().nullable(),
  tenantId: z.string().nullable(),
  createdAt: dateOrString,
  updatedAt: dateOrString,
  lessonCount: z.number(),
  enrollmentCount: z.number(),
  totalDuration: z.number().nullable(),
})

/** GET /api/courses/:id — コース詳細（tagIds付き） */
export const CourseResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  category: z.string(),
  level: z.string(),
  duration: z.string(),
  instructorId: z.string().nullable(),
  imageUrl: z.string().nullable(),
  isPublished: z.boolean(),
  markdownSummary: z.string().nullable(),
  evaluationPrompt: z.string().nullable().optional(),
  tenantId: z.string().nullable(),
  createdAt: dateOrString,
  updatedAt: dateOrString,
  tagIds: z.array(z.string()),
})

export type CourseWithStatsResponse = z.infer<typeof CourseWithStatsSchema>
export type CourseResponse = z.infer<typeof CourseResponseSchema>
